/**
 * Pro 2 scenes: the "Gaming / Entertainment / Productivity" TV section from viture.com/pro2 (its "s24").
 * Once the section reaches 78% down the viewport the room fades in and the TV rises into place; on
 * mobile the first headline then reveals word by word. The section then pins, and scroll hands the TV
 * from one scene to the next: the playing video darkens and shrinks as the next one opens from the
 * centre, while the copy lifts away and the next headline rises in word by word. A green spill glows
 * behind the TV through the entertainment scene. Each video loops only while its scene shows.
 * The fluid backdrop behind it is pro2-fluid.
 * Timeline positions, durations and easing are taken from the source page.
 */
if (!customElements.get('pro2-scenes')) {
  const MOBILE_QUERY = '(max-width: 640px)';
  const STAGES = { desktop: { width: 1920, height: 1080 }, mobile: { width: 375, height: 812 } };
  // Time constant (ms) of the lag behind the scroll position, matching the source's scrub: 0.6
  const SMOOTHING_MS = 200;
  // The pinned timeline runs from 0 to this; scene n hands over to n + 1 from n + HANDOVER_OFFSET
  const TIMELINE_LENGTH = 2;
  const HANDOVER_OFFSET = 0.225;
  // Mobile's first headline: each word starts REVEAL_STAGGER seconds after the previous one
  const REVEAL_DELAY = 0.3;
  const REVEAL_STAGGER = 0.075;
  // Incoming copy: its words rise in across this window of the timeline, each taking this share of it
  const WORDS_WINDOW = 0.26;
  const WORD_SHARE = 0.6;
  const WORD_RISE = 22;

  /**
   * @typedef {object} Scene
   * @property {HTMLElement} item The TV video for this scene
   * @property {HTMLElement} text Its headline and subheading
   * @property {HTMLElement[]} words Every word of the copy, headline then subheading
   */

  /**
   * @typedef {object} StageParts
   * @property {HTMLElement} glow
   * @property {Scene[]} scenes
   */

  /**
   * @param {number} value
   * @returns {number}
   */
  const clamp01 = (value) => Math.min(1, Math.max(0, value));

  /**
   * Progress of a tween that starts at `start` and lasts `duration`, on the pinned timeline
   * @param {number} time
   * @param {number} start
   * @param {number} duration
   * @returns {number}
   */
  const tweenProgress = (time, start, duration) => clamp01((time - start) / duration);

  // GSAP's power1–3 eases
  /** @param {number} progress */
  const easeInQuad = (progress) => progress * progress;
  /** @param {number} progress */
  const easeInCubic = (progress) => progress ** 3;
  /** @param {number} progress */
  const easeOutCubic = (progress) => 1 - (1 - progress) ** 3;
  /** @param {number} progress */
  const easeOutQuart = (progress) => 1 - (1 - progress) ** 4;

  /**
   * Show an element at this opacity, hiding it outright at zero like GSAP's autoAlpha
   * @param {HTMLElement} element
   * @param {number} opacity
   */
  const setAutoAlpha = (element, opacity) => {
    element.style.opacity = opacity.toFixed(4);
    element.style.visibility = opacity > 0 ? 'inherit' : 'hidden';
  };

  class Pro2Scenes extends HTMLElement {
    /** @type {HTMLElement} */
    track = document.createElement('div');
    /** @type {HTMLElement} */
    sticky = document.createElement('div');
    /** @type {MediaQueryList} */
    mobileQuery = window.matchMedia(MOBILE_QUERY);
    reducedMotion = false;
    /** @type {HTMLVideoElement[]} */
    videos = [];
    /** @type {HTMLElement[]} */
    headlines = [];
    /** @type {StageParts[]} */
    stages = [];
    /** Pinned progress (0–1) drawn now, easing toward `targetProgress` */
    currentProgress = 0;
    targetProgress = 0;
    hasWarmedVideos = false;
    /** @type {number | null} */
    animationFrame = null;
    /** @type {number | null} */
    lastTickTime = null;
    resizeFrame = 0;
    /** @type {IntersectionObserver | null} */
    revealObserver = null;

    connectedCallback() {
      this.track = /** @type {HTMLElement} */ (this.querySelector('.pro2-scenes__track'));
      this.sticky = /** @type {HTMLElement} */ (this.querySelector('.pro2-scenes__sticky'));
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.videos = Array.from(this.querySelectorAll('video'));
      this.headlines = Array.from(this.querySelectorAll('[data-gradient]'));

      this.stages = Array.from(this.querySelectorAll('[data-stage]')).map((stage) => {
        const items = Array.from(stage.querySelectorAll('[data-item]'));
        const texts = Array.from(stage.querySelectorAll('[data-text]'));
        return {
          glow: /** @type {HTMLElement} */ (stage.querySelector('[data-glow]')),
          scenes: texts.map((element, index) => {
            const text = /** @type {HTMLElement} */ (element);
            const words = Array.from(text.querySelectorAll('[data-split]')).flatMap((part) =>
              this.#splitWords(/** @type {HTMLElement} */ (part))
            );
            return { item: /** @type {HTMLElement} */ (items[index]), text, words };
          }),
        };
      });
      this.headlines.forEach((headline) => this.#paintGradient(headline));

      window.addEventListener('resize', this.onResize);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.mobileQuery.addEventListener('change', this.onResize);
      document.fonts?.ready.then(() => this.headlines.forEach((headline) => this.#paintGradient(headline)));
      this.#fitStage();

      if (this.reducedMotion) {
        this.videos.forEach((video) => {
          video.preload = 'none';
          video.pause();
        });
      } else {
        this.classList.add('is-armed');
        const observer = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this.classList.add('is-revealed');
            observer.disconnect();
          },
          { rootMargin: '0px 0px -22% 0px' }
        );
        observer.observe(this);
        this.revealObserver = observer;
      }

      this.currentProgress = this.#readProgress();
      this.targetProgress = this.currentProgress;
      this.#render();
      this.onScroll();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('scroll', this.onScroll);
      this.mobileQuery.removeEventListener('change', this.onResize);
      this.revealObserver?.disconnect();
      cancelAnimationFrame(this.resizeFrame);
      if (this.animationFrame !== null) cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }

    onResize = () => {
      cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = requestAnimationFrame(() => {
        this.#fitStage();
        this.headlines.forEach((headline) => this.#paintGradient(headline));
        this.onScroll();
      });
    };

    onScroll = () => {
      this.targetProgress = this.#readProgress();
      this.#updateVideos();
      if (this.animationFrame === null) this.animationFrame = requestAnimationFrame(this.tick);
    };

    /** Ease the drawn progress toward the scroll position, then stop once it has caught up */
    tick = (/** @type {number} */ now) => {
      const elapsed = Math.min(now - (this.lastTickTime ?? now), 64);
      this.lastTickTime = now;
      const next =
        this.currentProgress + (this.targetProgress - this.currentProgress) * (1 - Math.exp(-elapsed / SMOOTHING_MS));
      this.currentProgress = Math.abs(this.targetProgress - next) < 0.0005 ? this.targetProgress : next;
      this.#render();
      this.#updateVideos();

      if (this.currentProgress === this.targetProgress) {
        this.animationFrame = null;
        this.lastTickTime = null;
      } else {
        this.animationFrame = requestAnimationFrame(this.tick);
      }
    };

    /**
     * Progress across the pinned scroll, from the section top meeting the viewport top
     * @returns {number}
     */
    #readProgress() {
      const trackRect = this.track.getBoundingClientRect();
      const pinDistance = trackRect.height - this.sticky.clientHeight;
      if (pinDistance <= 0) return trackRect.top <= 0 ? 1 : 0;
      return clamp01(-trackRect.top / pinDistance);
    }

    /** Desktop covers the viewport; mobile is 375 wide and as tall as the viewport at that width */
    #fitStage() {
      const viewportWidth = window.innerWidth || this.sticky.clientWidth;
      const viewportHeight = this.sticky.clientHeight;
      if (this.mobileQuery.matches) {
        const scale = viewportWidth / STAGES.mobile.width;
        this.style.setProperty('--stage-h', (viewportHeight / scale).toFixed(2));
        this.style.setProperty('--scale', scale.toFixed(4));
      } else {
        const { width, height } = STAGES.desktop;
        const scale = Math.max(viewportWidth / width, viewportHeight / height);
        this.style.setProperty('--stage-h', String(height));
        this.style.setProperty('--scale', scale.toFixed(4));
      }
    }

    /**
     * Wrap each word in a span, keeping the spaces between them
     * @param {HTMLElement} element
     * @returns {HTMLElement[]} The word spans
     */
    #splitWords(element) {
      const text = element.textContent ?? '';
      const fragment = document.createDocumentFragment();
      /** @type {HTMLElement[]} */
      const words = [];
      text.split(/(\s+)/).forEach((part) => {
        if (!part) return;
        if (/^\s+$/.test(part)) {
          fragment.append(document.createTextNode(part));
          return;
        }
        const word = document.createElement('span');
        word.className = 'pro2-scenes__word';
        word.textContent = part;
        const delay = REVEAL_DELAY + words.length * REVEAL_STAGGER;
        word.style.setProperty('--word-delay', `${delay.toFixed(3)}s`);
        words.push(word);
        fragment.append(word);
      });
      element.replaceChildren(fragment);
      element.classList.add('is-split');
      return words;
    }

    /**
     * Give each word its slice of one gradient spanning the whole headline
     * @param {HTMLElement} headline
     */
    #paintGradient(headline) {
      if (!headline.offsetWidth) return;
      const gradientSize = `${headline.offsetWidth}px 100%`;
      const headlineRect = headline.getBoundingClientRect();
      // Measured on screen, so take off the stage scale and each word's slide-in `left`
      const scale = headlineRect.width / headline.offsetWidth || 1;
      headline.querySelectorAll('.pro2-scenes__word').forEach((element) => {
        const word = /** @type {HTMLElement} */ (element);
        const slide = parseFloat(getComputedStyle(word).left) || 0;
        const offsetInHeadline = (word.getBoundingClientRect().left - headlineRect.left) / scale - slide;
        word.style.backgroundSize = gradientSize;
        word.style.backgroundPosition = `${-offsetInHeadline}px 0`;
      });
    }

    #render() {
      const time = this.currentProgress * TIMELINE_LENGTH;

      this.stages.forEach(({ glow, scenes }) => {
        scenes.forEach((scene, index) => this.#renderScene(scene, index, scenes.length, time));

        // The green spill swells in behind the entertainment scene, then fades as it hands over
        const glowIn = easeOutCubic(tweenProgress(time, 0.705, 0.28));
        const glowOut = easeInQuad(tweenProgress(time, 1.225, 0.25));
        const isFading = time >= 1.225;
        setAutoAlpha(glow, isFading ? 1 - glowOut : glowIn);
        glow.style.scale = (isFading ? 1 - 0.04 * glowOut : 0.9 + 0.1 * glowIn).toFixed(4);
      });
    }

    /**
     * Draw one scene: it opens in as the previous scene hands over, and hands over to the next
     * @param {Scene} scene
     * @param {number} index
     * @param {number} sceneCount
     * @param {number} time Position on the pinned timeline
     */
    #renderScene({ item, text, words }, index, sceneCount, time) {
      const enterAt = index > 0 ? index - 1 + HANDOVER_OFFSET : null;
      const exitAt = index < sceneCount - 1 ? index + HANDOVER_OFFSET : null;

      // TV video: opens from the centre, then darkens and shrinks away behind the next one
      let itemOpacity = 1;
      let itemScale = 1;
      let clipInset = 0;
      let brightness = 1;
      if (enterAt !== null) {
        const opening = easeOutQuart(tweenProgress(time, enterAt + 0.08, 0.38));
        itemOpacity = tweenProgress(time, enterAt + 0.04, 0.02);
        itemScale = 0.94 + 0.06 * opening;
        clipInset = 50 * (1 - opening);
      }
      if (exitAt !== null && time >= exitAt) {
        const closing = easeInQuad(tweenProgress(time, exitAt, 0.3));
        itemOpacity *= 1 - tweenProgress(time, exitAt + 0.46, 0.02);
        itemScale = 1 - 0.03 * closing;
        brightness = 1 - closing;
      }
      setAutoAlpha(item, itemOpacity);
      item.style.scale = itemScale.toFixed(4);
      item.style.clipPath = clipInset > 0 ? `inset(${clipInset.toFixed(3)}% ${clipInset.toFixed(3)}%)` : '';
      item.style.filter = brightness < 1 ? `brightness(${brightness.toFixed(4)})` : '';

      // Copy: rises in with its words, then lifts away
      let textOpacity = 1;
      let textOffset = 0;
      if (enterAt !== null) {
        const entering = easeOutQuart(tweenProgress(time, enterAt + 0.3, 0.22));
        textOpacity = entering;
        textOffset = 40 * (1 - entering);

        const wordDuration = WORDS_WINDOW * WORD_SHARE;
        const wordStagger = words.length > 1 ? (WORDS_WINDOW - wordDuration) / (words.length - 1) : 0;
        words.forEach((word, wordIndex) => {
          const rising = easeOutCubic(tweenProgress(time, enterAt + 0.3 + wordStagger * wordIndex, wordDuration));
          word.style.opacity = rising.toFixed(4);
          word.style.top = `${(WORD_RISE * (1 - rising)).toFixed(2)}px`;
        });
      }
      if (exitAt !== null && time >= exitAt + 0.02) {
        const leaving = easeInCubic(tweenProgress(time, exitAt + 0.02, 0.18));
        textOpacity = 1 - leaving;
        textOffset = -40 * leaving;
      }
      setAutoAlpha(text, textOpacity);
      text.style.translate = `0 ${textOffset.toFixed(2)}px`;
    }

    /** Buffer the videos a screen ahead; loop each one only while its scene shows */
    #updateVideos() {
      if (this.reducedMotion || !this.videos.length) return;
      const viewportHeight = window.innerHeight;
      const sectionRect = this.getBoundingClientRect();
      const isNear = sectionRect.bottom > -viewportHeight && sectionRect.top < 2 * viewportHeight;

      this.videos.forEach((video) => {
        if (isNear && !this.hasWarmedVideos) {
          video.preload = 'auto';
          video.load();
        }
        const item = video.closest('[data-item]');
        const isShown =
          video.offsetParent !== null && (!(item instanceof HTMLElement) || item.style.visibility !== 'hidden');
        if (isNear && isShown) {
          if (video.paused) video.play().catch(() => {});
        } else if (!video.paused) {
          video.pause();
        }
      });

      if (isNear) this.hasWarmedVideos = true;
    }
  }

  customElements.define('pro2-scenes', Pro2Scenes);
}
